import CoinIcon from '../components/CoinIcon';
import WalletIcon from '../components/WalletIcon';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api from '../api';
import { FiArrowLeft, FiArrowDown, FiArrowUp, FiCreditCard, FiArrowUpCircle, FiClock, FiPlus, FiCheck, FiUpload, FiTrash2, FiX } from 'react-icons/fi';

const Wallet = () => {
  const navigate = useNavigate();
  const { user, showToast } = useAuth();

  const [hostWallet, setHostWallet] = useState(null);
  const [hostWalletTxns, setHostWalletTxns] = useState([]);
  const [hostBanking, setHostBanking] = useState(null);
  const [bankingForm, setBankingForm] = useState({ bank_name: '', account_holder: '', account_number: '', branch_code: '', account_type: 'cheque' });
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [kycFile, setKycFile] = useState(null);
  const [kycDocType, setKycDocType] = useState('id_book');
  const [uploadingKyc, setUploadingKyc] = useState(false);
  const [walletSubTab, setWalletSubTab] = useState('overview');
  const [kycStatus, setKycStatus] = useState(null);
  const [kycRejectionReason, setKycRejectionReason] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardForm, setCardForm] = useState({ card_last4: '', card_brand: 'visa', card_holder: '', expiry_month: '', expiry_year: '' });
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpPaymentMethodId, setTopUpPaymentMethodId] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);

  const loadWallet = async () => {
    try {
      const [walletRes, bankingRes] = await Promise.all([
        api.get('/host/wallet'),
        api.get('/host/banking'),
      ]);
      setHostWallet(walletRes.data);
      setHostWalletTxns(walletRes.data?.transactions || []);
      setHostBanking(bankingRes.data || null);
      if (bankingRes.data?.kyc_status) setKycStatus(bankingRes.data.kyc_status);
      if (bankingRes.data?.kyc_rejection_reason) setKycRejectionReason(bankingRes.data.kyc_rejection_reason);
    } catch {}
  };

  const loadPaymentMethods = async () => {
    try {
      const r = await api.get('/host/payment-methods');
      setPaymentMethods(r.data || []);
    } catch {}
  };

  const submitKycDocuments = async () => {
    if (!kycFile) return showToast('Please select a document to upload', 'error');
    setUploadingKyc(true);
    try {
      const fd = new FormData();
      fd.append('document_type', kycDocType);
      fd.append('front', kycFile);
      await api.post('/user/upload-kyc', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      showToast('KYC document submitted for review');
      setKycStatus('pending');
      setKycRejectionReason('');
      setKycFile(null);
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to upload document', 'error');
    } finally {
      setUploadingKyc(false);
    }
  };

  useEffect(() => { loadWallet(); loadPaymentMethods(); }, []);

  // Wallet is only for hosts, not vendors
  if (user?.role === 'vendor') {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <button onClick={() => navigate('/dashboard')} style={{
          background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rose)', fontSize: '14px', fontWeight: 600,
          display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '24px'
        }}>
          <FiArrowLeft size={16} /> Back to Dashboard
        </button>
        <p style={{ color: 'var(--gray)', fontSize: '14px' }}>The wallet is only available for hosts.</p>
      </div>
    );
  }

  return (
    <div>
      <style>{`@keyframes ggrSpin{to{transform:rotate(360deg)}}`}</style>

      {/* Back to Dashboard */}
      <button onClick={() => navigate('/dashboard')} style={{
        background: 'none', border: 'none', cursor: 'pointer', color: 'var(--rose)', fontSize: '14px', fontWeight: 600,
        display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '20px', padding: '8px 0'
      }}>
        <FiArrowLeft size={16} /> Back to Dashboard
      </button>

      {/* Host Wallet — Premium Digital Wallet */}
      <div className="card" style={{ marginBottom: '32px', padding: '0', overflow: 'hidden' }}>
        {/* Gradient wallet card — bank card style */}
        <div style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #2d1b3d 50%, #1a1a2e 100%)',
          padding: '28px 28px 20px', position: 'relative', overflow: 'hidden'
        }}>
          {/* Decorative circles */}
          <div style={{ position: 'absolute', top: -30, right: -20, width: 140, height: 140, borderRadius: '50%', background: 'rgba(232,160,191,0.08)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -40, left: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(212,168,83,0.06)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '4px' }}>My Wallet</div>
                <div style={{ fontSize: '32px', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                  R{Number(hostWallet?.available_balance || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginTop: '2px' }}>Available Balance</div>
              </div>
              <div style={{ width: '32px', height: '32px' }} />
            </div>
            {/* Quick stats row */}
            <div style={{ display: 'flex', gap: '24px' }}>
              <div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>Total Earned</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>R{Number(hostWallet?.total_earned || 0).toLocaleString()}</div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>Withdrawn</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>R{Number(hostWallet?.total_withdrawn || 0).toLocaleString()}</div>
              </div>
              <div>
                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>KYC</div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: hostBanking?.kyc_status === 'approved' ? '#4ade80' : hostBanking?.kyc_status === 'pending' ? '#fbbf24' : 'rgba(255,255,255,0.5)' }}>
                  {hostBanking?.kyc_status === 'approved' ? 'Verified' : hostBanking?.kyc_status === 'pending' ? 'Pending' : 'Unverified'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick action buttons */}
        <div style={{ display: 'flex', gap: '10px', padding: '16px 24px', borderBottom: '1px solid var(--cream-dark)' }}>
          <button onClick={() => { setWalletSubTab('cards'); setTopUpAmount(''); }} style={{
            flex: 1, padding: '10px', borderRadius: '10px', background: 'linear-gradient(135deg, #E8A0BF, #D4A853)',
            color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
          }}>
            <FiArrowDown size={16} /> Top Up
          </button>
          <button onClick={() => setWalletSubTab('withdraw')} style={{
            flex: 1, padding: '10px', borderRadius: '10px',
            background: 'var(--cream)', color: 'var(--dark)', border: '1px solid var(--cream-dark)',
            cursor: 'pointer', fontSize: '13px', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
          }}>
            <FiArrowUp size={16} /> Withdraw
          </button>
          <button onClick={() => setWalletSubTab('cards')} style={{
            flex: 1, padding: '10px', borderRadius: '10px',
            background: 'var(--cream)', color: 'var(--dark)', border: '1px solid var(--cream-dark)',
            cursor: 'pointer', fontSize: '13px', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
          }}>
            <FiCreditCard size={16} /> Cards
          </button>
        </div>

        {/* Sub-tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--cream-dark)', padding: '0 24px', gap: '0' }}>
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'cards', label: 'Payment Methods' },
            { key: 'banking', label: 'Banking + KYC' },
            { key: 'withdraw', label: 'Withdraw' },
          ].map(t => (
            <button key={t.key} onClick={() => setWalletSubTab(t.key)} style={{
              padding: '10px 18px', fontSize: '13px', fontWeight: walletSubTab === t.key ? 600 : 400,
              background: 'none', border: 'none',
              borderBottom: walletSubTab === t.key ? '2px solid var(--rose)' : '2px solid transparent',
              color: walletSubTab === t.key ? 'var(--rose)' : 'var(--gray)',
              cursor: 'pointer', transition: 'all 0.2s'
            }}>{t.label}</button>
          ))}
        </div>

        <div style={{ padding: '24px' }}>
          {/* OVERVIEW SUB-TAB */}
          {walletSubTab === 'overview' && (
            <div>
              {hostWallet ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                  <div style={{ padding: '16px', borderRadius: '12px', background: 'var(--cream)', textAlign: 'center' }}>
                    <FiArrowDown size={16} color="var(--gray)" style={{ marginBottom: '6px' }} />
                    <div style={{ fontSize: '11px', color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Available</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--dark)' }}>R{Number(hostWallet.available_balance || 0).toLocaleString()}</div>
                  </div>
                  <div style={{ padding: '16px', borderRadius: '12px', background: 'var(--cream)', textAlign: 'center' }}>
                    <WalletIcon size={16} color="var(--gray)" style={{ marginBottom: '6px' }} />
                    <div style={{ fontSize: '11px', color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Total Earned</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--dark)' }}>R{Number(hostWallet.total_earned || 0).toLocaleString()}</div>
                  </div>
                  <div style={{ padding: '16px', borderRadius: '12px', background: 'var(--cream)', textAlign: 'center' }}>
                    <FiArrowUp size={16} color="var(--gray)" style={{ marginBottom: '6px' }} />
                    <div style={{ fontSize: '11px', color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Withdrawn</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--dark)' }}>R{Number(hostWallet.total_withdrawn || 0).toLocaleString()}</div>
                  </div>
                  <div style={{ padding: '16px', borderRadius: '12px', background: 'var(--cream)', textAlign: 'center' }}>
                    <FiCreditCard size={16} color="var(--gray)" style={{ marginBottom: '6px' }} />
                    <div style={{ fontSize: '11px', color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Saved Cards</div>
                    <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--dark)' }}>{paymentMethods.length}</div>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <CoinIcon size={48} color="var(--gray)" style={{ opacity: 0.3 }} />
                  <p style={{ color: 'var(--gray)', marginTop: '12px' }}>No wallet activity yet. Your earnings will appear here.</p>
                </div>
              )}

              {/* Recent transactions */}
              {hostWalletTxns.length > 0 ? (
                <div>
                  <h4 style={{ fontSize: '14px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiClock size={14} /> Recent Transactions
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {hostWalletTxns.slice(0, 8).map((txn, i) => {
                      const isCredit = txn.type === 'credit' || txn.type === 'top_up' || txn.amount > 0;
                      return (
                      <div key={txn.id || i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 14px', borderRadius: '10px', background: 'var(--cream)',
                        fontSize: '13px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '34px', height: '34px', borderRadius: '50%',
                            background: isCredit ? 'rgba(16,185,129,0.1)' : 'rgba(255,107,107,0.08)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                          }}>
                            {isCredit ? <FiArrowDown size={14} color="#10B981" /> : <FiArrowUp size={14} color="#FF6B6B" />}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500 }}>{txn.description || txn.type || 'Transaction'}</div>
                            <div style={{ fontSize: '11px', color: 'var(--gray)' }}>
                              {txn.created_at ? new Date(txn.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                              {txn.status === 'completed' ? '' : ` · ${txn.status}`}
                            </div>
                          </div>
                        </div>
                        <span style={{ fontWeight: 600, color: isCredit ? '#10B981' : 'var(--dark)' }}>
                          {isCredit ? '+' : '-'}R{Math.abs(txn.amount || 0).toLocaleString()}
                        </span>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ) : hostWallet ? (
                <p style={{ textAlign: 'center', color: 'var(--gray)', fontSize: '13px', padding: '20px 0' }}>No transactions yet.</p>
              ) : null}
            </div>
          )}

          {/* PAYMENT METHODS SUB-TAB */}
          {walletSubTab === 'cards' && (
            <div>
              {/* Saved cards list */}
              {paymentMethods.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ fontSize: '14px', marginBottom: '10px' }}>Saved Cards</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {paymentMethods.map((pm) => (
                      <div key={pm.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 14px', borderRadius: '10px', background: 'var(--cream)',
                        border: pm.is_default ? '1px solid rgba(212,168,83,0.3)' : '1px solid transparent'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '38px', height: '38px', borderRadius: '8px',
                            background: 'linear-gradient(135deg, #1a1a2e, #2d1b3d)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                          }}>
                            <FiCreditCard size={18} color="#fff" />
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '13px', textTransform: 'capitalize' }}>
                              {pm.card_brand} <span style={{ fontWeight: 400, color: 'var(--gray)' }}>****{pm.card_last4}</span>
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--gray)' }}>
                              {pm.card_holder || 'No name'} {pm.expiry_month ? `· Exp ${String(pm.expiry_month).padStart(2,'0')}/${pm.expiry_year || '--'}` : ''}
                              {pm.is_default ? <span style={{ color: '#D4A853', fontWeight: 600, marginLeft: '6px' }}>Default</span> : null}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {!pm.is_default && (
                            <button onClick={async () => {
                              try { await api.put(`/host/payment-methods/${pm.id}/default`); loadPaymentMethods(); showToast('Default card updated'); } catch {}
                            }} style={{
                              background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)',
                              padding: '4px', fontSize: '11px'
                            }} title="Set as default">Set default</button>
                          )}
                          <button onClick={async () => {
                            if (!window.confirm('Remove this card?')) return;
                            try { await api.delete(`/host/payment-methods/${pm.id}`); loadPaymentMethods(); showToast('Card removed'); } catch (e) { showToast('Failed to remove', 'error'); }
                          }} style={{
                            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)',
                            padding: '4px'
                          }}><FiTrash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add new card */}
              {showAddCard ? (
                <div style={{ padding: '16px', borderRadius: '12px', background: 'var(--cream)', marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '14px', marginBottom: '12px' }}>Add New Card</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: '12px' }}>Card Number (last 4 digits)</label>
                      <input value={cardForm.card_last4} onChange={e => setCardForm({ ...cardForm, card_last4: e.target.value.replace(/\D/g,'').slice(0,4) })} placeholder="1234" maxLength={4} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', boxSizing: 'border-box' }} />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: '12px' }}>Card Brand</label>
                      <select value={cardForm.card_brand} onChange={e => setCardForm({ ...cardForm, card_brand: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', background: 'var(--white)' }}>
                        <option value="visa">Visa</option>
                        <option value="mastercard">Mastercard</option>
                        <option value="amex">American Express</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: '12px' }}>Card Holder</label>
                      <input value={cardForm.card_holder} onChange={e => setCardForm({ ...cardForm, card_holder: e.target.value })} placeholder="Name on card" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', boxSizing: 'border-box' }} />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: '12px' }}>Expiry Month</label>
                      <input type="number" value={cardForm.expiry_month} onChange={e => setCardForm({ ...cardForm, expiry_month: e.target.value })} placeholder="MM" min="1" max="12" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', boxSizing: 'border-box' }} />
                    </div>
                    <div className="form-group">
                      <label style={{ fontSize: '12px' }}>Expiry Year</label>
                      <input type="number" value={cardForm.expiry_year} onChange={e => setCardForm({ ...cardForm, expiry_year: e.target.value })} placeholder="YYYY" min="2026" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                    <button onClick={async () => {
                      if (!cardForm.card_last4 || cardForm.card_last4.length < 4) return showToast('Enter last 4 digits', 'error');
                      try {
                        await api.post('/host/payment-methods', { ...cardForm, expiry_month: Number(cardForm.expiry_month) || null, expiry_year: Number(cardForm.expiry_year) || null });
                        showToast('Card added!');
                        setCardForm({ card_last4: '', card_brand: 'visa', card_holder: '', expiry_month: '', expiry_year: '' });
                        setShowAddCard(false);
                        loadPaymentMethods();
                      } catch (e) { showToast(e.response?.data?.error || 'Failed to add card', 'error'); }
                    }} style={{
                      padding: '9px 20px', borderRadius: '8px', background: 'var(--rose)', color: '#fff',
                      border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600
                    }}>Save Card</button>
                    <button onClick={() => { setShowAddCard(false); setCardForm({ card_last4: '', card_brand: 'visa', card_holder: '', expiry_month: '', expiry_year: '' }); }} style={{
                      padding: '9px 20px', borderRadius: '8px', background: 'var(--cream)', color: 'var(--gray)',
                      border: '1px solid var(--cream-dark)', cursor: 'pointer', fontSize: '13px'
                    }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowAddCard(true)} style={{
                  padding: '12px', borderRadius: '10px', width: '100%',
                  background: 'var(--cream)', color: 'var(--dark)', border: '2px dashed rgba(0,0,0,0.1)',
                  cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  marginBottom: '20px'
                }}>
                  <FiPlus size={16} /> Add New Card
                </button>
              )}

              {/* Top-up section */}
              <div style={{ borderTop: '1px solid var(--cream-dark)', paddingTop: '20px' }}>
                <h4 style={{ fontSize: '14px', marginBottom: '4px' }}>Top Up Wallet</h4>
                <p style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '12px' }}>
                  Add funds to your wallet using a saved payment method.
                </p>

                {/* Preset amounts */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '14px' }}>
                  {[50, 100, 200, 500].map(amt => (
                    <button key={amt} onClick={() => setTopUpAmount(String(amt))} style={{
                      padding: '10px', borderRadius: '8px', textAlign: 'center',
                      background: topUpAmount === String(amt) ? 'var(--rose)' : 'var(--cream)',
                      color: topUpAmount === String(amt) ? '#fff' : 'var(--dark)',
                      border: topUpAmount === String(amt) ? '2px solid var(--rose)' : '1px solid var(--cream-dark)',
                      cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                      transition: 'all 0.2s'
                    }}>R{amt}</button>
                  ))}
                </div>

                {/* Custom amount */}
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px' }}>Custom Amount (R)</label>
                  <input type="number" value={topUpAmount} onChange={e => setTopUpAmount(e.target.value)} placeholder="Enter amount" min="1" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>

                {/* Payment method selector */}
                {paymentMethods.length > 0 && (
                  <div className="form-group" style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '12px' }}>Pay With</label>
                    <select value={topUpPaymentMethodId} onChange={e => setTopUpPaymentMethodId(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', background: 'var(--white)' }}>
                      <option value="">Select a card...</option>
                      {paymentMethods.map(pm => (
                        <option key={pm.id} value={pm.id}>{pm.card_brand} ****{pm.card_last4}{pm.is_default ? ' (Default)' : ''}</option>
                      ))}
                    </select>
                  </div>
                )}

                <button onClick={async () => {
                  const amt = Number(topUpAmount);
                  if (!amt || amt <= 0) return showToast('Enter a valid amount', 'error');
                  if (paymentMethods.length > 0 && !topUpPaymentMethodId) return showToast('Select a payment method', 'error');
                  setTopUpLoading(true);
                  try {
                    const body = { amount: amt };
                    if (topUpPaymentMethodId) body.payment_method_id = Number(topUpPaymentMethodId);
                    await api.post('/host/wallet/top-up', body);
                    showToast(`Wallet topped up with R${amt.toLocaleString()}!`);
                    setTopUpAmount('');
                    setTopUpPaymentMethodId('');
                    loadWallet();
                  } catch (e) { showToast(e.response?.data?.error || 'Top-up failed', 'error'); }
                  finally { setTopUpLoading(false); }
                }} disabled={topUpLoading || !topUpAmount || Number(topUpAmount) <= 0} style={{
                  padding: '12px 28px', borderRadius: '10px', width: '100%',
                  background: topUpLoading ? 'var(--gray)' : 'linear-gradient(135deg, #E8A0BF, #D4A853)',
                  color: '#fff', border: 'none',
                  cursor: topUpLoading || !topUpAmount ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  opacity: topUpLoading || !topUpAmount ? 0.6 : 1
                }}>
                  {topUpLoading ? (
                    <><div style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'ggrSpin 0.8s linear infinite' }} /> Processing...</>
                  ) : (
                    <><FiArrowDown size={16} /> Top Up Wallet</>
                  )}
                </button>

                {paymentMethods.length === 0 && (
                  <p style={{ fontSize: '11px', color: 'var(--gray)', textAlign: 'center', marginTop: '8px' }}>
                    Add a card above to enable quick top-ups. You can also top up manually without a saved card.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* BANKING + KYC SUB-TAB */}
          {walletSubTab === 'banking' && (
            <div>
              {/* KYC Section */}
              <div style={{ marginBottom: '28px' }}>
                <h4 style={{ fontSize: '15px', marginBottom: '4px' }}>KYC Verification</h4>
                <p style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '16px' }}>
                  Upload a clear photo or scan of your ID document, passport, or driver's license.
                </p>

                {/* KYC Status Badge */}
                {kycStatus && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '6px 14px', borderRadius: '20px', marginBottom: '16px',
                    fontSize: '12px', fontWeight: 600,
                    background: kycStatus === 'approved' ? 'rgba(16,185,129,0.1)' : kycStatus === 'rejected' ? 'rgba(255,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                    color: kycStatus === 'approved' ? '#10B981' : kycStatus === 'rejected' ? '#FF4444' : '#F59E0B'
                  }}>
                    {kycStatus === 'approved' ? <FiCheck size={14} /> : kycStatus === 'rejected' ? <FiX size={14} /> : <FiClock size={14} />}
                    {kycStatus === 'approved' ? 'Verified' : kycStatus === 'rejected' ? 'Rejected' : 'Pending Review'}
                  </div>
                )}
                {kycRejectionReason && (
                  <p style={{ fontSize: '12px', color: '#FF4444', marginTop: '-8px', marginBottom: '12px' }}>
                    Reason: {kycRejectionReason}. Please re-upload.
                  </p>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label style={{ fontSize: '13px' }}>Document Type</label>
                    <select value={kycDocType} onChange={e => setKycDocType(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', background: 'var(--white)' }}>
                      <option value="id_book">ID Book</option>
                      <option value="passport">Passport</option>
                      <option value="driver_license">Driver's License</option>
                    </select>
                  </div>
                </div>
                <div className="form-group" style={{ marginTop: '12px' }}>
                  <label style={{ fontSize: '13px' }}>Upload Document</label>
                  {kycFile ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 12px', borderRadius: '8px', background: 'var(--cream)', fontSize: '12px' }}>
                      <FiCheck size={14} color="#10B981" />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{kycFile.name}</span>
                      <button onClick={() => setKycFile(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray)' }}><FiX size={14} /></button>
                    </div>
                  ) : (
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '14px', borderRadius: '8px', border: '2px dashed rgba(0,0,0,0.15)', cursor: 'pointer', fontSize: '12px', color: 'var(--gray)' }}>
                      <FiUpload size={14} /> Choose file
                      <input type="file" accept="image/*" onChange={e => { if (e.target.files[0]) setKycFile(e.target.files[0]); }} style={{ display: 'none' }} />
                    </label>
                  )}
                </div>
                <button
                  onClick={submitKycDocuments}
                  disabled={uploadingKyc || !kycFile}
                  style={{
                    marginTop: '14px', padding: '10px 24px', borderRadius: '10px',
                    background: uploadingKyc ? 'var(--gray)' : 'var(--rose)',
                    color: 'white', border: 'none', cursor: uploadingKyc ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px'
                  }}
                >
                  {uploadingKyc ? (
                    <><div style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', animation: 'ggrSpin 0.8s linear infinite' }} /> Uploading...</>
                  ) : (
                    <><FiUpload size={14} /> Submit for Review</>
                  )}
                </button>
              </div>

              {/* Banking Details Section */}
              <div>
                <h4 style={{ fontSize: '15px', marginBottom: '4px' }}>Banking Details</h4>
                <p style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '16px' }}>
                  Add your bank account for withdrawals.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label style={{ fontSize: '13px' }}>Bank Name</label>
                    <input
                      value={bankingForm.bank_name}
                      onChange={e => setBankingForm({ ...bankingForm, bank_name: e.target.value })}
                      placeholder="e.g., FNB, Standard Bank"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '13px' }}>Account Holder</label>
                    <input
                      value={bankingForm.account_holder}
                      onChange={e => setBankingForm({ ...bankingForm, account_holder: e.target.value })}
                      placeholder="Your full name"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '13px' }}>Account Number</label>
                    <input
                      value={bankingForm.account_number}
                      onChange={e => setBankingForm({ ...bankingForm, account_number: e.target.value.replace(/\D/g, '') })}
                      placeholder="e.g., 1234567890"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '13px' }}>Branch Code</label>
                    <input
                      value={bankingForm.branch_code}
                      onChange={e => setBankingForm({ ...bankingForm, branch_code: e.target.value })}
                      placeholder="e.g., 250655"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div className="form-group">
                    <label style={{ fontSize: '13px' }}>Account Type</label>
                    <select
                      value={bankingForm.account_type}
                      onChange={e => setBankingForm({ ...bankingForm, account_type: e.target.value })}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', background: 'var(--white)' }}
                    >
                      <option value="cheque">Cheque / Current</option>
                      <option value="savings">Savings</option>
                      <option value="transmission">Transmission</option>
                    </select>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    if (!bankingForm.bank_name || !bankingForm.account_holder || !bankingForm.account_number) return;
                    try {
                      await api.post('/host/banking', bankingForm);
                      showToast('Banking details saved!');
                      loadWallet();
                    } catch (e) {
                      showToast(e.response?.data?.error || 'Failed to save banking details', 'error');
                    }
                  }}
                  style={{
                    marginTop: '14px', padding: '10px 24px', borderRadius: '10px',
                    background: 'var(--dark)', color: 'white', border: 'none',
                    cursor: (!bankingForm.bank_name || !bankingForm.account_holder || !bankingForm.account_number) ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: 600, opacity: (!bankingForm.bank_name || !bankingForm.account_holder || !bankingForm.account_number) ? 0.5 : 1
                  }}
                  disabled={!bankingForm.bank_name || !bankingForm.account_holder || !bankingForm.account_number}
                >
                  <FiCreditCard size={14} style={{ marginRight: '6px' }} /> Save Banking Details
                </button>
                {hostBanking && (
                  <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(16,185,129,0.06)', fontSize: '12px', color: '#10B981', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FiCheck size={14} /> Banking details on file: {hostBanking.bank_name} — {hostBanking.account_holder}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* WITHDRAW SUB-TAB */}
          {walletSubTab === 'withdraw' && (
            <div>
              <h4 style={{ fontSize: '15px', marginBottom: '4px' }}>Withdraw Funds</h4>
              <p style={{ fontSize: '12px', color: 'var(--gray)', marginBottom: '16px' }}>
                Transfer your available balance to your bank account.
              </p>

              {hostBanking?.kyc_status !== 'approved' ? (
                <div style={{
                  padding: '20px', borderRadius: '12px', textAlign: 'center',
                  background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)'
                }}>
                  <FiClock size={32} color="#F59E0B" style={{ marginBottom: '8px' }} />
                  <p style={{ fontSize: '13px', color: '#92400E', margin: '0 0 4px', fontWeight: 600 }}>
                    KYC Verification Required
                  </p>
                  <p style={{ fontSize: '12px', color: '#92400E', margin: '0 0 12px' }}>
                    Please complete KYC verification in the Banking + KYC tab before withdrawing.
                  </p>
                  <button
                    onClick={() => setWalletSubTab('banking')}
                    style={{
                      padding: '8px 18px', borderRadius: '8px', background: '#F59E0B', color: 'white',
                      border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600
                    }}
                  >
                    Go to Banking + KYC
                  </button>
                </div>
              ) : !hostBanking?.bank_name ? (
                <div style={{
                  padding: '20px', borderRadius: '12px', textAlign: 'center',
                  background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)'
                }}>
                  <FiCreditCard size={32} color="#3B82F6" style={{ marginBottom: '8px' }} />
                  <p style={{ fontSize: '13px', color: '#1E40AF', margin: '0 0 4px', fontWeight: 600 }}>
                    Banking Details Required
                  </p>
                  <p style={{ fontSize: '12px', color: '#1E40AF', margin: '0 0 12px' }}>
                    Please add your banking details before withdrawing.
                  </p>
                  <button
                    onClick={() => setWalletSubTab('banking')}
                    style={{
                      padding: '8px 18px', borderRadius: '8px', background: '#3B82F6', color: 'white',
                      border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600
                    }}
                  >
                    Go to Banking Details
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{
                    padding: '16px', borderRadius: '12px', background: 'var(--cream)',
                    marginBottom: '20px', textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '11px', color: 'var(--gray)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Available Balance</div>
                    <div style={{ fontSize: '28px', fontWeight: 700, color: 'var(--dark)' }}>
                      R{Number(hostWallet?.available_balance || 0).toLocaleString()}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label style={{ fontSize: '13px' }}>Amount (R)</label>
                      <input
                        type="number"
                        value={withdrawAmount}
                        onChange={e => setWithdrawAmount(e.target.value)}
                        placeholder="Enter amount"
                        min="1"
                        max={hostWallet?.available_balance || 0}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', fontSize: '14px', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <p style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '6px' }}>
                    Withdrawing to: {hostBanking.bank_name} — ...{hostBanking.account_number?.slice(-4) || '****'}
                  </p>
                  <button
                    onClick={async () => {
                      const amt = Number(withdrawAmount);
                      if (!amt || amt <= 0) return;
                      if (amt > (hostWallet?.available_balance || 0)) {
                        showToast('Insufficient balance', 'error');
                        return;
                      }
                      try {
                        await api.post('/host/wallet/withdraw', { amount: amt });
                        showToast(`R${amt} withdrawal requested!`);
                        setWithdrawAmount('');
                        loadWallet();
                      } catch (e) {
                        showToast(e.response?.data?.error || 'Withdrawal failed', 'error');
                      }
                    }}
                    disabled={!withdrawAmount || Number(withdrawAmount) <= 0 || Number(withdrawAmount) > (hostWallet?.available_balance || 0)}
                    style={{
                      marginTop: '14px', padding: '12px 32px', borderRadius: '10px',
                      background: 'var(--rose)', color: 'white', border: 'none',
                      cursor: (!withdrawAmount || Number(withdrawAmount) <= 0) ? 'not-allowed' : 'pointer',
                      fontSize: '14px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px',
                      opacity: (!withdrawAmount || Number(withdrawAmount) <= 0) ? 0.5 : 1
                    }}
                  >
                    <FiArrowUpCircle size={16} /> Withdraw Funds
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Wallet;
